import {
  ACCOUNT_MERGE_PREVIEW_LOADING,
  ACCOUNT_MERGE_PREVIEW_SUCCESS,
  ACCOUNT_MERGE_PREVIEW_FAILURE,
  ACCOUNT_MERGE_SWAP_LOADING,
  ACCOUNT_MERGE_SWAP_SUCCESS,
  ACCOUNT_MERGE_SWAP_FAILURE,
  ACCOUNT_MERGE_SWAP_RESET,
  ACCOUNT_MERGE_EXECUTING,
  ACCOUNT_MERGE_SUCCESS,
  ACCOUNT_MERGE_FAILURE,
  ACCOUNT_MERGE_RESET,
  ACCOUNT_MERGE_VERIFYING,
  ACCOUNT_MERGE_VERIFY_SUCCESS,
  ACCOUNT_MERGE_VERIFY_FAILURE,
  ACCOUNT_MERGE_VERIFY_TIMEOUT
} from "../actions/actionTypes";

// --- sessionStorage helpers for surviving page reload ---
const MERGE_SESSION_KEY = "accountMergeState";

function saveMergeSession(previewData, authCode) {
  try {
    if (typeof sessionStorage === "undefined") return;
    sessionStorage.setItem(
      MERGE_SESSION_KEY,
      JSON.stringify({ previewData, authCode })
    );
  } catch {
    // Storage full or unavailable — silently degrade
  }
}

function loadMergeSession() {
  try {
    if (typeof sessionStorage === "undefined") return null;
    const raw = sessionStorage.getItem(MERGE_SESSION_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed && parsed.previewData != null && parsed.authCode != null) {
      return parsed;
    }
    // Invalid shape — clean up so we don't re-read bad data on next reload
    clearMergeSession();
  } catch {
    // Corrupted data — clear it
    clearMergeSession();
  }
  return null;
}

function clearMergeSession() {
  try {
    if (typeof sessionStorage === "undefined") return;
    sessionStorage.removeItem(MERGE_SESSION_KEY);
  } catch {
    // Ignore
  }
}

// Static defaults — never contains hydrated values.
// Used by ACCOUNT_MERGE_RESET so it can return a clean slate
// without worrying about restored sessionStorage leaking through.
const defaultState = {
  // Loading states
  isLoadingPreview: false,
  isLoadingSwap: false,
  isExecutingMerge: false,
  isVerifying: false,

  // Data
  previewData: null,
  authCode: null,

  // Swap state — intentionally NOT persisted to sessionStorage.
  // The swap is a cheap client-side toggle, and swappedEligibility
  // could be stale after a reload, so we always start fresh.
  isSwapped: false,
  swappedEligibility: null,

  // Error state
  error: null,

  // Flow state
  showMergePage: false,
  mergeCompleted: false
};

// Hydrate from sessionStorage so the merge page survives a reload
const restored = loadMergeSession();

const initialState = {
  ...defaultState,
  previewData: restored ? restored.previewData : null,
  authCode: restored ? restored.authCode : null,
  showMergePage: !!restored
};

export default function accountMergeReducer(state = initialState, action) {
  switch (action.type) {
    case ACCOUNT_MERGE_PREVIEW_LOADING:
      return {
        ...state,
        isLoadingPreview: true,
        error: null
      };

    case ACCOUNT_MERGE_PREVIEW_SUCCESS:
      saveMergeSession(action.payload.previewData, action.payload.authCode);
      return {
        ...state,
        isLoadingPreview: false,
        previewData: action.payload.previewData,
        authCode: action.payload.authCode,
        showMergePage: true,
        error: null
      };

    case ACCOUNT_MERGE_PREVIEW_FAILURE:
      clearMergeSession();
      return {
        ...state,
        isLoadingPreview: false,
        error: action.payload.error,
        showMergePage: false
      };

    case ACCOUNT_MERGE_SWAP_LOADING:
      return {
        ...state,
        isLoadingSwap: true,
        error: null
      };

    case ACCOUNT_MERGE_SWAP_SUCCESS:
      return {
        ...state,
        isLoadingSwap: false,
        isSwapped: true,
        swappedEligibility: action.payload.swappedEligibility
      };

    case ACCOUNT_MERGE_SWAP_FAILURE:
      return {
        ...state,
        isLoadingSwap: false,
        error: action.payload.error
      };

    case ACCOUNT_MERGE_SWAP_RESET:
      // Intentionally preserve swappedEligibility so we can use the cached
      // result if the user swaps again, avoiding a redundant API call.
      // Clear any error (e.g. from a prior failed swap check) so it doesn't
      // persist when the user returns to the original merge direction.
      return {
        ...state,
        isSwapped: false,
        error: null
      };

    case ACCOUNT_MERGE_EXECUTING:
      return {
        ...state,
        isExecutingMerge: true,
        error: null
      };

    case ACCOUNT_MERGE_SUCCESS:
      clearMergeSession();
      return {
        ...state,
        isExecutingMerge: false,
        mergeCompleted: true,
        showMergePage: false,
        error: null
      };

    // NOTE: ACCOUNT_MERGE_FAILURE intentionally does NOT clear sessionStorage.
    // A failed merge execution shows an error toast and the user can retry
    // from the same page. Clearing the session would force them to restart
    // the entire OAuth flow to get a new auth code.
    case ACCOUNT_MERGE_FAILURE:
      return {
        ...state,
        isExecutingMerge: false,
        error: action.payload.error
      };

    case ACCOUNT_MERGE_RESET:
      clearMergeSession();
      return defaultState;

    case ACCOUNT_MERGE_VERIFYING:
      return {
        ...state,
        isVerifying: true,
        error: null
      };

    case ACCOUNT_MERGE_VERIFY_SUCCESS:
      return {
        ...state,
        isVerifying: false,
        error: null
      };

    // VERIFY_FAILURE and VERIFY_TIMEOUT share the same state transition.
    // They are separate action types so the action creators can fire
    // distinct GTM analytics events and toast messages for each outcome.
    case ACCOUNT_MERGE_VERIFY_FAILURE:
    case ACCOUNT_MERGE_VERIFY_TIMEOUT:
      return {
        ...state,
        isVerifying: false,
        error: action.payload.error
      };

    default:
      return state;
  }
}
